import React from 'react';

import { cn } from '@/lib/utils';

interface Props {
  testId: string;
  code: string;
  /** The currency's own symbol, shown in the badge. */
  symbol: string;
  /** The row count, already worded. */
  rowsLabel: string;
  /** The spend in this currency, already formatted. */
  amount: string;
  /** Tints the badge and captions the amount with `groupCurrencyLabel`. */
  isGroupCurrency: boolean;
  groupCurrencyLabel: string;
  /** The converted figure, already worded; `null` while no rate has resolved. */
  convertedLabel: string | null;
}

/** What a file spends in one currency, and what the import will make of it. */
export function CurrencySpendRow({
  testId,
  code,
  symbol,
  rowsLabel,
  amount,
  isGroupCurrency,
  groupCurrencyLabel,
  convertedLabel,
}: Props) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 px-4 py-3">
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-semibold',
          symbol.length > 2 ? 'text-xs' : 'text-base',
          isGroupCurrency
            ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
        )}
      >
        {symbol}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-gray-900 dark:text-white">
          {code}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{rowsLabel}</p>
      </div>
      <div className="flex max-w-[60%] flex-col items-end">
        <span className="text-base font-bold text-gray-900 dark:text-white">
          {amount}
        </span>
        {isGroupCurrency ? (
          <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
            {groupCurrencyLabel}
          </span>
        ) : (
          convertedLabel !== null && (
            <span
              data-testid={`${testId}-converted`}
              className="text-right text-xs text-gray-500 dark:text-gray-400"
            >
              {convertedLabel}
            </span>
          )
        )}
      </div>
    </div>
  );
}
